import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Star, PawPrint } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const ReviewsCarousel = ({ reviews }) => {
  const [index, setIndex] = useState(0);
  const [active, setActive] = useState(null);
  const perPage = 4;
  const totalPages = Math.max(1, Math.ceil(reviews.length / perPage));

  const next = () => setIndex((i) => (i + 1) % totalPages);
  const prev = () => setIndex((i) => (i - 1 + totalPages) % totalPages);

  const visible = reviews.slice(index * perPage, index * perPage + perPage);

  const gradients = [
    'from-[#e870b6] to-[#d65aa1]',
    'from-[#a78bfa] to-[#7c6df0]',
    'from-[#f9a8d4] to-[#e870b6]',
    'from-[#5b3eb5] to-[#7c6df0]',
  ];

  return (
    <div className="relative">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {visible.map((r, i) => (
          <div
            key={r.id}
            className="bg-white rounded-2xl shadow-md hover:shadow-2xl border border-[#f3d6f5]/50 p-5 transition-all hover:-translate-y-1 flex sm:flex-col gap-4 sm:gap-0"
          >
            {/* Avatar — compact circle on mobile, big square gradient on desktop */}
            <div className={`rounded-full sm:rounded-xl w-16 h-16 sm:w-auto sm:h-auto sm:aspect-square sm:mb-5 bg-gradient-to-br ${gradients[i % gradients.length]} flex items-center justify-center relative overflow-hidden flex-shrink-0`}>
              <PawPrint className="hidden sm:block absolute top-3 right-3 w-5 h-5 text-white/30" />
              <span className="font-serif text-[28px] sm:text-[80px] font-bold text-white drop-shadow-md sm:drop-shadow-lg">{r.initial}</span>
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-bold text-[16px] sm:text-[18px] text-[#1a0b3d] mb-1 sm:mb-2">@{r.name}</h4>
              <div className="flex gap-0.5 sm:gap-1 mb-2 sm:mb-3">
                {[...Array(r.rating)].map((_, i) => (
                  <Star key={i} size={14} className="fill-amber-400 text-amber-400 sm:w-4 sm:h-4" />
                ))}
              </div>
              <p className="text-slate-600 text-[13px] sm:text-[14px] leading-relaxed">
                {r.short}{' '}
                <button
                  className="text-[#e870b6] font-semibold hover:underline"
                  onClick={() => setActive(r)}
                >
                  Read More
                </button>
              </p>
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-3 mt-8">
          <Button
            variant="outline"
            size="icon"
            className="rounded-full border-[#5b3eb5] text-[#5b3eb5] hover:bg-[#5b3eb5] hover:text-white"
            onClick={prev}
          >
            <ChevronLeft size={18} />
          </Button>
          <div className="flex gap-2">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  i === index ? 'bg-[#5b3eb5] w-6' : 'bg-slate-300'
                }`}
                onClick={() => setIndex(i)}
              />
            ))}
          </div>
          <Button
            variant="outline"
            size="icon"
            className="rounded-full border-[#5b3eb5] text-[#5b3eb5] hover:bg-[#5b3eb5] hover:text-white"
            onClick={next}
          >
            <ChevronRight size={18} />
          </Button>
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-[24px] text-[#1a0b3d]">@{active.name}</DialogTitle>
              </DialogHeader>
              <div className="flex gap-1">
                {[...Array(active.rating)].map((_, i) => (
                  <Star key={i} size={18} className="fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-slate-600 leading-relaxed">{active.full}</p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReviewsCarousel;
